import { useCallback, useEffect, useState } from 'react';
import { httpGet } from '../../components/utils';

export type QueryParams = Record<string, string>;

export interface UseGetParams {
    url: string;
    queryParams?: QueryParams;
    enabled?: boolean;
}

export interface UseGetReturnType<TData> {
    data?: TData;
    error: unknown;
    loading: boolean;
    fetchData: () => Promise<void>;
}

const getFullUrl = (url: string, queryParams: QueryParams) => {
    if (!Object.keys(queryParams).length) {
        return url;
    }

    const searchParams = new URLSearchParams(queryParams);

    return `${url}?${searchParams.toString()}`;
};

export const useGet = <TData>({ url, queryParams = {}, enabled = true }: UseGetParams): UseGetReturnType<TData> => {
    const [data, setData] = useState<TData>();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const fullUrl = getFullUrl(url, queryParams);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(undefined);

        try {
            const { data } = await httpGet(fullUrl);
            setData(data as unknown as TData);
        } catch (error) {
            setError(error);
        }

        setLoading(false);
    }, [fullUrl]);

    useEffect(() => {
        if (enabled) {
            fetchData();
        }
    }, [enabled, fetchData]);

    return {
        data,
        error,
        loading,
        fetchData
    };
};
